/** @file Server → client WebSocket frames (contracts/ws-protocol.md) and their tolerant parser. */
import type { Patch } from 'immer';
import type { RenderEntry } from '@pose2d/api/render/renderEntry';
import type { CommitSummary } from '@pose2d/api/types';
import { toPose2DError } from '@pose2d/format/toPose2DError';
import type { Pose2DError } from '@pose2d/format/types';

/** One actor online and the entity name it is editing. */
export interface ActorPresence {
  name: string;
  editing: string | null;
}

/** Every frame the ProjectDO sends; a commit's `patches` is null when the server said `fetch: true`. */
export type ServerFrame =
  | { t: 'hello'; rev: number; actors: ActorPresence[] }
  | { t: 'commit'; rev: number; actor: string; summary: CommitSummary; patches: Patch[] | null }
  | { t: 'presence'; actor: string; editing: string | null }
  | { t: 'leave'; actor: string }
  | { t: 'pong'; rev: number }
  | { t: 'resync' }
  | { t: 'render'; entry: RenderEntry }
  | { t: 'renders'; entries: RenderEntry[]; more: boolean }
  | { t: 'error'; error: Pose2DError };

type Raw = Record<string, unknown>;

const isRev = (v: unknown): v is number => typeof v === 'number' && Number.isSafeInteger(v) && v >= 0;
const isEditing = (v: unknown): v is string | null => v === null || typeof v === 'string';
const isActor = (v: unknown): v is ActorPresence =>
  typeof v === 'object' && v !== null && typeof (v as Raw)['name'] === 'string' && isEditing((v as Raw)['editing']);
const isSummary = (v: unknown, rev: number): v is CommitSummary =>
  typeof v === 'object' && v !== null && (v as Raw)['rev'] === rev && Array.isArray((v as Raw)['commands']);

const isEntry = (v: unknown): v is RenderEntry =>
  typeof v === 'object' && v !== null && isRev((v as Raw)['seq']) && typeof (v as Raw)['url'] === 'string' && typeof (v as Raw)['actor'] === 'string';

const commit = ({ rev, actor, summary, patches }: Raw): ServerFrame | null =>
  isRev(rev) && typeof actor === 'string' && isSummary(summary, rev)
    ? { t: 'commit', rev, actor, summary, patches: Array.isArray(patches) ? (patches as Patch[]) : null }
    : null;

/** One parser per frame type. */
const PARSERS: Record<string, (f: Raw) => ServerFrame | null> = {
  hello: ({ rev, actors }) => (isRev(rev) && Array.isArray(actors) ? { t: 'hello', rev, actors: actors.filter(isActor) } : null),
  commit,
  presence: ({ actor, editing }) => (typeof actor === 'string' && isEditing(editing) ? { t: 'presence', actor, editing } : null),
  leave: ({ actor }) => (typeof actor === 'string' ? { t: 'leave', actor } : null),
  pong: ({ rev }) => (isRev(rev) ? { t: 'pong', rev } : null),
  resync: () => ({ t: 'resync' }),
  render: ({ entry }) => (isEntry(entry) ? { t: 'render', entry } : null),
  renders: ({ entries, more }) => (Array.isArray(entries) && typeof more === 'boolean' ? { t: 'renders', entries: entries.filter(isEntry), more } : null),
  error: ({ error }) => {
    const parsed = toPose2DError({ pose2d: error });
    return parsed === null ? null : { t: 'error', error: parsed };
  },
};

/** The frame in `text`, or null for anything that is not JSON or not a known, well-formed frame. */
export function parseServerFrame(text: string): ServerFrame | null {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    return null;
  }
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const { t } = value as Raw;
  const parse = typeof t === 'string' && Object.hasOwn(PARSERS, t) ? PARSERS[t] : undefined;
  return parse ? parse(value as Raw) : null;
}
